import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, Chip, Stack } from '@mui/material';

interface ExerciseMuscleMapProps {
  // Режим 1: Прост - подчертаване на мускули по упражнение
  muscleGroup?: string;
  primaryMuscleGroup?: string;
  secondaryMuscleGroup?: string;
  tertiaryMuscleGroup?: string;
  // Режим 2: Стойности - показване на числа с пречупени стрелки
  muscleValues?: Record<string, number | string>;
  // Скриване на анотациите (стрелки + числа) — полезно за компактен изглед/принт
  hideAnnotations?: boolean;
  printMode?: boolean;
}

// Калибрирани за 700px контейнер
const REFERENCE_WIDTH = 700;
const ASPECT_RATIO = 741 / 807; // height / width на контейнера

interface MaskOverlay {
  muscleGroup: string;
  spriteCol: number;
  spriteRow: number;
  top: number;
  left: number;
  width: number;
  height: number;
  x: number;
  y: number;
  backgroundSizeWigth: number;
  backgroundSizeHeight: number;
}

const maskOverlays: MaskOverlay[] = [
  { muscleGroup: 'shoulders', spriteCol: 0, spriteRow: 0, top: 0, left: 0,  width: 50, height: 100, x: -6, y: 0, backgroundSizeWigth: 500, backgroundSizeHeight: 200 },
  { muscleGroup: 'back',      spriteCol: 1, spriteRow: 0, top: 0, left: 50, width: 50, height: 100, x: -359, y: 0, backgroundSizeWigth: 500, backgroundSizeHeight: 200 },
  { muscleGroup: 'chest',     spriteCol: 2, spriteRow: 0, top: 0, left: 0,  width: 50, height: 100, x: -1360, y: -612, backgroundSizeWigth: 500, backgroundSizeHeight: 200 },
  { muscleGroup: 'arms',      spriteCol: 3, spriteRow: 0, top: 0, left: 0,  width: 100, height: 100, x: -687.5, y: 0, backgroundSizeWigth: 249.5, backgroundSizeHeight: 200 },
  { muscleGroup: 'core',      spriteCol: 4, spriteRow: 0, top: 0, left: 0,  width: 50, height: 100, x: 0, y: 0, backgroundSizeWigth: 500, backgroundSizeHeight: 200 },
  { muscleGroup: 'quadriceps', spriteCol: 0, spriteRow: 1, top: 0, left: 0,  width: 100, height: 100, x: -10, y: -608, backgroundSizeWigth: 249.5, backgroundSizeHeight: 200 },
  { muscleGroup: 'hamstrings', spriteCol: 0, spriteRow: 1, top: 0, left: 0,  width: 100, height: 100, x: -10, y: -608, backgroundSizeWigth: 249.5, backgroundSizeHeight: 200 },
  { muscleGroup: 'adductors',  spriteCol: 0, spriteRow: 1, top: 0, left: 0,  width: 100, height: 100, x: -10, y: -608, backgroundSizeWigth: 249.5, backgroundSizeHeight: 200 },
  { muscleGroup: 'glutes',    spriteCol: 1, spriteRow: 1, top: 0, left: 50, width: 50, height: 100, x: 0, y: 0, backgroundSizeWigth: 500, backgroundSizeHeight: 200 },
  { muscleGroup: 'calves',    spriteCol: 3, spriteRow: 1, top: 0, left: 0,  width: 50, height: 100, x: 0, y: 0, backgroundSizeWigth: 500, backgroundSizeHeight: 200 },
];

const muscleGroupLabels: Record<string, string> = {
  'chest': 'Гръдни',
  'back': 'Гръб',
  'quadriceps': 'Квадрицепс',
  'hamstrings': 'Задно бедро',
  'adductors': 'Аддуктори',
  'shoulders': 'Рамене',
  'arms': 'Ръце',
  'core': 'Корем',
  'glutes': 'Глутеуси',
  'calves': 'Икри',
};

// ============================================================
// КОНФИГУРАЦИЯ НА АНОТАЦИИТЕ (стрелки + числа)
// ============================================================
// За всяка мускулна група:
//   side: 'left' | 'right' - от коя страна е етикетът
//   anchorX/anchorY: % позиция върху тялото, където стрелката сочи
//   labelY: % вертикална позиция на етикета
// ============================================================
interface AnnotationConfig {
  label: string;
  side: 'left' | 'right';
  anchorX: number;
  anchorY: number;
  labelY: number;
}

// ---- РЕДАКТИРАЙТЕ ПОЗИЦИИТЕ НА СТРЕЛКИТЕ ТУК ----
const annotationConfig: Record<string, AnnotationConfig> = {
  shoulders: { label: 'Рамене',   side: 'left',  anchorX: 17, anchorY: 15, labelY: 6  },
  chest:     { label: 'Гръдни',   side: 'left',  anchorX: 24, anchorY: 26, labelY: 22 },
  arms:      { label: 'Ръце',     side: 'left',  anchorX: 9,  anchorY: 35, labelY: 38 },
  core:      { label: 'Корем',    side: 'left',  anchorX: 25, anchorY: 44, labelY: 52 },
  quadriceps: { label: 'Квадрицепс',  side: 'left',  anchorX: 22, anchorY: 60, labelY: 60 },
  hamstrings: { label: 'Задно бедро', side: 'left',  anchorX: 20, anchorY: 66, labelY: 70 },
  adductors:  { label: 'Аддуктори',   side: 'left',  anchorX: 27, anchorY: 58, labelY: 80 },
  back:      { label: 'Гръб',     side: 'right', anchorX: 72, anchorY: 20, labelY: 12 },
  glutes:    { label: 'Глутеуси', side: 'right', anchorX: 70, anchorY: 50, labelY: 42 },
  calves:    { label: 'Икри',     side: 'right', anchorX: 78, anchorY: 78, labelY: 72 },
};

// Колко % от ширината на тялото стрелката излиза навън (към етикетите)
const CONNECTOR_EXTEND = 22;
const LINE_COLOR = 'rgba(0,0,0,0.25)';
const LINE_WIDTH = '1.5px';
const CORNER_RADIUS = 6;
const DOT_SIZE = 5;

const ExerciseMuscleMap: React.FC<ExerciseMuscleMapProps> = ({
  muscleGroup,
  primaryMuscleGroup,
  secondaryMuscleGroup,
  tertiaryMuscleGroup,
  muscleValues,
  hideAnnotations,
  printMode = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      setContainerWidth(entries[0].contentRect.width);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const isValuesMode = !!muscleValues && Object.keys(muscleValues).length > 0;

  // Кои мускули да се подчертаят и с каква интензивност
  const highlightedMuscles: Record<string, number> = {};
  if (isValuesMode) {
    Object.keys(muscleValues!).forEach(group => {
      if (group !== 'cardio') highlightedMuscles[group] = 0.85;
    });
  } else {
    const primary = primaryMuscleGroup || muscleGroup;
    if (primary && primary !== 'cardio') highlightedMuscles[primary] = 1.0;
    if (secondaryMuscleGroup && secondaryMuscleGroup !== 'cardio') highlightedMuscles[secondaryMuscleGroup] = 0.55;
    if (tertiaryMuscleGroup && tertiaryMuscleGroup !== 'cardio') highlightedMuscles[tertiaryMuscleGroup] = 0.3;
  }

  // Когато контейнерът е скрит (display:none), containerWidth е 0.
  // НЕ рендираме маските докато нямаме реална ширина, за да избегнем разместване.
  const scaleFactor = containerWidth > 0 ? containerWidth / REFERENCE_WIDTH : 0;

  if (Object.keys(highlightedMuscles).length === 0) return null;

  const primary = primaryMuscleGroup || muscleGroup;

  return (
    <Box
      className={`exercise-muscle-map${printMode ? ' exercise-muscle-map--print' : ''}`}
      sx={{ mt: printMode ? 0 : 2 }}
    >
      {/* Прост режим: чипове */}
      {!isValuesMode && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1, justifyContent: 'center' }}>
          {primary && primary !== 'cardio' && (
            <Chip
              label={muscleGroupLabels[primary] || primary}
              size="small"
              sx={{ bgcolor: 'rgba(255, 107, 107, 0.8)', color: '#fff', fontWeight: 'bold', fontSize: '0.7rem' }}
            />
          )}
          {secondaryMuscleGroup && secondaryMuscleGroup !== 'cardio' && (
            <Chip
              label={muscleGroupLabels[secondaryMuscleGroup] || secondaryMuscleGroup}
              size="small"
              sx={{ bgcolor: 'rgba(255, 160, 122, 0.7)', color: '#fff', fontWeight: 'bold', fontSize: '0.7rem' }}
            />
          )}
          {tertiaryMuscleGroup && tertiaryMuscleGroup !== 'cardio' && (
            <Chip
              label={muscleGroupLabels[tertiaryMuscleGroup] || tertiaryMuscleGroup}
              size="small"
              sx={{ bgcolor: 'rgba(255, 200, 150, 0.7)', color: '#fff', fontWeight: 'bold', fontSize: '0.7rem' }}
            />
          )}
        </Stack>
      )}

      {/* Диаграма */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        px: isValuesMode ? (printMode ? '6%' : '14%') : 0,
      }}>
        {/* Позициониращ контейнер - overflow: visible за етикетите */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: isValuesMode ? (printMode ? 460 : 400) : 350,
            aspectRatio: '807 / 741',
          }}
        >
          {/* Тяло + маски (overflow: hidden за маските) */}
          <Box
            ref={containerRef}
            sx={{
              position: 'absolute',
              inset: 0,
              overflow: 'hidden',
              borderRadius: 1,
              border: '1px solid',
              borderColor: printMode ? '#d6dbe1' : 'divider',
              bgcolor: printMode ? '#f8fbf9' : '#1a1a1a',
            }}
          >
            <Box
              component="img"
              src="/assets/body/main-image.png"
              alt="Мускулни групи"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block',
                filter: printMode
                  ? 'brightness(1.02) contrast(1.05) saturate(0.95)'
                  : 'brightness(0.65) saturate(0.8)',
              }}
            />

            {maskOverlays.map((mask, index) => {
              const opacity = highlightedMuscles[mask.muscleGroup];
              if (!opacity) return null;

              // Процентно позициониране — скалира автоматично без JavaScript
              // Формула: pct = 10000 * pixel / (REF_WIDTH * elementSize% * (1 - bgSize_fraction))
              const bgW = mask.backgroundSizeWigth / 100; // fraction, e.g. 500% → 5
              const bgH = mask.backgroundSizeHeight / 100;
              const pctX = mask.x === 0 ? 0 : 10000 * mask.x / (REFERENCE_WIDTH * mask.width * (1 - bgW));
              const pctY = mask.y === 0 ? 0 : 10000 * mask.y / (REFERENCE_WIDTH * ASPECT_RATIO * mask.height * (1 - bgH));

              return (
                <Box
                  key={`mask-${mask.muscleGroup}-${index}`}
                  className="muscle-mask-overlay"
                  sx={{
                    position: 'absolute',
                    top: `${mask.top}%`,
                    left: `${mask.left}%`,
                    width: `${mask.width}%`,
                    height: `${mask.height}%`,
                    backgroundImage: 'url(/assets/body/muscle-groups-masks.png)',
                    backgroundSize: `${mask.backgroundSizeWigth}% ${mask.backgroundSizeHeight}%`,
                    backgroundPosition: `${pctX}% ${pctY}%`,
                    opacity: opacity * (printMode ? 0.82 : 0.7),
                    filter: printMode ? 'saturate(0.9) contrast(1.05)' : 'saturate(0.6)',
                    pointerEvents: 'none',
                    transition: 'opacity 0.3s ease',
                  }}
                />
              );
            })}
          </Box>

          {/* Анотации: пречупени стрелки + числа */}
          {isValuesMode && !hideAnnotations && Object.entries(muscleValues!).map(([group, value]) => {
            const config = annotationConfig[group];
            if (!config || group === 'cardio') return null;

            const { side, anchorX, anchorY, labelY, label } = config;
            const isBelow = anchorY >= labelY;
            const topY = Math.min(anchorY, labelY);
            const height = Math.abs(anchorY - labelY) || 1;

            // Стил на пречупената линия (L-shape connector)
            const connectorSx = side === 'left'
              ? {
                  // Хоризонтална линия от лявата страна → до anchorX,
                  // после вертикална надолу (или нагоре) до котвата
                  left: `${-CONNECTOR_EXTEND}%`,
                  top: `${topY}%`,
                  width: `${anchorX + CONNECTOR_EXTEND}%`,
                  height: `${height}%`,
                  borderRight: `${LINE_WIDTH} solid ${LINE_COLOR}`,
                  ...(isBelow
                    ? { borderTop: `${LINE_WIDTH} solid ${LINE_COLOR}`, borderTopRightRadius: CORNER_RADIUS }
                    : { borderBottom: `${LINE_WIDTH} solid ${LINE_COLOR}`, borderBottomRightRadius: CORNER_RADIUS }
                  ),
                }
              : {
                  // Хоризонтална линия от anchorX → до дясната страна,
                  // после вертикална надолу (или нагоре) до котвата
                  left: `${anchorX}%`,
                  top: `${topY}%`,
                  width: `${100 - anchorX + CONNECTOR_EXTEND}%`,
                  height: `${height}%`,
                  borderLeft: `${LINE_WIDTH} solid ${LINE_COLOR}`,
                  ...(isBelow
                    ? { borderTop: `${LINE_WIDTH} solid ${LINE_COLOR}`, borderTopLeftRadius: CORNER_RADIUS }
                    : { borderBottom: `${LINE_WIDTH} solid ${LINE_COLOR}`, borderBottomLeftRadius: CORNER_RADIUS }
                  ),
                };

            return (
              <React.Fragment key={`ann-${group}`}>
                {/* Пречупена стрелка */}
                <Box
                  sx={{
                    position: 'absolute',
                    ...connectorSx,
                    pointerEvents: 'none',
                  }}
                />

                {/* Точка на котвата */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${anchorX}%`,
                    top: `${anchorY}%`,
                    width: DOT_SIZE,
                    height: DOT_SIZE,
                    borderRadius: '50%',
                    bgcolor: LINE_COLOR,
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                  }}
                />

                {/* Етикет с числото */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: `${labelY}%`,
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                    ...(side === 'left'
                      ? {
                          right: `${100 + CONNECTOR_EXTEND + 1}%`,
                          textAlign: 'right',
                        }
                      : {
                          left: `${100 + CONNECTOR_EXTEND + 1}%`,
                          textAlign: 'left',
                        }
                    ),
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      lineHeight: 1.1,
                      color: 'text.primary',
                    }}
                  >
                    {typeof value === 'number' ? (value % 1 === 0 ? value : value.toFixed(1)) : value}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.6rem',
                      lineHeight: 1,
                      color: '#999',
                      display: 'block',
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default ExerciseMuscleMap;
